import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/doctocat/doctocat/theme/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<p>{`When you build your Doctocat site using `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/docs/gatsby-cli/#build"
      }}><inlineCode parentName="a">{`gatsby build`}</inlineCode></a>{`, the output is static content: HTML, CSS, JavaScript, images, etc. Static content is incredibly easy and affordable to host with any deployment solution. We have landed on using `}<a parentName="p" {...{
        "href": "https://zeit.co/now"
      }}>{`Now`}</a>{` for our deploys.`}</p>
<h2>{`Deploying to Now`}</h2>
<p>{`If you started with the `}<a parentName="p" {...{
        "href": "https://github.com/primer/doctocat-template"
      }}>{`Doctocat template`}</a>{`, your site is already configured to deploy with Now. You're ready to set up the `}<a parentName="p" {...{
        "href": "https://zeit.co/github"
      }}>{`Now for GitHub`}</a>{` integration. If you did not start with the Doctocat template, before you set up the Now for Github integration, you'll need to create a `}<inlineCode parentName="p">{`now.json`}</inlineCode>{` file and add a `}<inlineCode parentName="p">{`now-build`}</inlineCode>{` script to `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`// now.json
{
  "version": 2,
  "name": "my-site",
  "builds": [
    {
      "src": "package.json",
      "use": "@now/static-build",
      "config": {"distDir": "public"}
    }
  ]
}
`}</code></pre>
<pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`// package.json
{
  "scripts": {
    ...
    "build": "gatsby build",
    "now-build": "npm run build"
  }
}
`}</code></pre>
<p>{`Once your site has the necessary configuration files and scripts, you can enable the Now for GitHub integration. You'll need to create a Now account, connect your GitHub account, then enable the integration for your repository. Check out the `}<a parentName="p" {...{
        "href": "https://zeit.co/docs/v2/integrations/now-for-github/"
      }}>{`Now for GitHub docs`}</a>{` for more detailed instructions.`}</p>
<p>{`After you have the GitHub integration set up, you should be able to access your site from a prefixed `}<inlineCode parentName="p">{`.now.sh`}</inlineCode>{` domain.`}</p>
<h2>{`Deploying to other providers`}</h2>
<p>{`Check out Gatsby's `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/docs/deploying-and-hosting/"
      }}>{`Deploying and Hosting`}</a>{` guides for more information about deploying to other providers.`}</p>
<h2>{`Adding a primer.style path alias`}</h2>
<h3>{`1. Update gatsby-config.js`}</h3>
<p>{`To make your site available from `}<inlineCode parentName="p">{`primer.style/my-site`}</inlineCode>{`, start by setting a `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/docs/path-prefix/"
      }}><inlineCode parentName="a">{`pathPrefix`}</inlineCode></a>{` in your `}<inlineCode parentName="p">{`gatsby-config.js`}</inlineCode>{`:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`// gatsby-config.js
module.exports = {
+ pathPrefix: '/my-site',
}
`}</code></pre>
<h3>{`2. Update package.json`}</h3>
<p>{`Next, make sure your `}<inlineCode parentName="p">{`build`}</inlineCode>{` script in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` runs `}<inlineCode parentName="p">{`gatsby build`}</inlineCode>{` with the `}<inlineCode parentName="p">{`--prefix-paths`}</inlineCode>{` flag:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`// package.json
{
  "scripts": {
-   "build": "gatsby build",
+   "build": "gatsby build --prefix-paths",
    "now-build": "npm run build"
  }
}
`}</code></pre>
<p>{`If this flag is not set, Gatsby will ignore your `}<inlineCode parentName="p">{`pathPrefix`}</inlineCode>{`.`}</p>
<h3>{`3. Update now.json`}</h3>
<p>{`The `}<inlineCode parentName="p">{`--prefix-paths`}</inlineCode>{` flag tells Gatsby to prefix paths used in the `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/docs/gatsby-link/"
      }}><inlineCode parentName="a">{`Link`}</inlineCode></a>{` component and `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/docs/gatsby-link/#how-to-use-the-navigate-helper-function"
      }}><inlineCode parentName="a">{`navigate`}</inlineCode></a>{` helper with the `}<inlineCode parentName="p">{`pathPrefix`}</inlineCode>{` value assigned in your `}<inlineCode parentName="p">{`gatsby-config.js`}</inlineCode>{` file. However, `}<inlineCode parentName="p">{`--prefix-paths`}</inlineCode>{` doesn't change where built files get stored in the build directory. This creates a mismatch between the links on your site and the actual paths to your files.`}</p>
<p>{`To fix this issue, you'll need to define a `}<a parentName="p" {...{
        "href": "https://zeit.co/docs/v2/advanced/routes"
      }}>{`route`}</a>{` in your `}<inlineCode parentName="p">{`now.json`}</inlineCode>{` file that proxies `}<inlineCode parentName="p">{`/my-site/*`}</inlineCode>{` to `}<inlineCode parentName="p">{`/*`}</inlineCode>{` so that behind the scenes a path like `}<inlineCode parentName="p">{`/my-site/getting-started`}</inlineCode>{` will find `}<inlineCode parentName="p">{`/getting-started.html`}</inlineCode>{` in your build directory:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`// now.json
{
  ...
  "routes": [
    {"src": "/my-site(/.*)?", "dest": "$1"},
  ]
}
`}</code></pre>
<p>{`If your site is located in a subdirectory, make sure the destination (`}<inlineCode parentName="p">{`dest`}</inlineCode>{`) of your proxy route is the path to that subdirectory followed by `}<inlineCode parentName="p">{`$1`}</inlineCode>{`. Here's what your `}<inlineCode parentName="p">{`now.json`}</inlineCode>{` file might look like if your site is located in a subdirectory called `}<inlineCode parentName="p">{`docs`}</inlineCode>{`:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`// now.json
{
  ...
  "routes": [
    {"src": "/my-site(/.*)?", "dest": "/docs$1"},
  ]
}
`}</code></pre>
<p>{`In order to route branch deploys correctly, you'll also need to set up a route that redirects root route requests (`}<inlineCode parentName="p">{`my-site.now.sh`}</inlineCode>{`) to your path prefix (`}<inlineCode parentName="p">{`my-site.now.sh/my-site`}</inlineCode>{`):`}</p>
<pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`// now.json
{
  ...
  "routes": [
    {"src": "/my-site(/.*)?", "dest": "/docs$1"},
+   {
+     "src": "/",
+     "status": 301,
+     "headers": {"Location": "/my-site"}
+   }
  ]
}
`}</code></pre>
<p>{`This is necessary because when your project lives in a subdirectory, there isn't any content for the site in the root of your deploy.`}</p>
<h3>{`4. Update now.json in primer/primer.style`}</h3>
<p>{`After everything is set up in your repository, open a pull request on the `}<a parentName="p" {...{
        "href": "https://github.com/primer/primer.style"
      }}>{`primer/primer.style`}</a>{` repository to add your site to the `}<inlineCode parentName="p">{`routes`}</inlineCode>{` list in `}<a parentName="p" {...{
        "href": "https://github.com/primer/primer.style/blob/master/now.json"
      }}>{`now.json`}</a>{`:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`// now.json in primer/primer.style
{
  "routes": [
    {"src": "/components(/.*)?", "dest": "https://primer-components.now.sh"},
    {"src": "/css(/.*)?", "dest": "https://primer-css.now.sh"},
    {"src": "/design(/.*)?", "dest": "https://primer-design.now.sh"},
    {"src": "/blueprints(/.*)?", "dest": "https://primer-blueprints.now.sh"},
    {"src": "/presentations(/.*)?", "dest": "https://primer-presentations.now.sh"},
+   {"src": "/my-site(/.*)?", "dest": "https://my-site.now.sh"}
  ]
}
`}</code></pre>
<p>{`Once your pull request is approved and merged, you'll be able to access your site from `}<inlineCode parentName="p">{`primer.style/my-site`}</inlineCode>{`. 🎉`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      